'use client'

import { useState, useEffect } from 'react'
import posthog from 'posthog-js'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Cookie, X, ChevronDown, ChevronUp, Shield } from 'lucide-react'
import Link from 'next/link'
import { isNativePlatform, resetPlatformCache } from '@/lib/utils/platform'
import { safeGetItem, safeSetItem, safeRemoveItem } from '@/lib/utils/safe-storage'
import { useTranslations } from 'next-intl'

// Cookie preference keys
const COOKIE_CONSENT_KEY = 'alumnia-cookie-consent'
const COOKIE_PREFERENCES_KEY = 'alumnia-cookie-preferences'

export interface CookiePreferences {
  essential: true // Always true, required
  functional: boolean
  analytics: boolean
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  functional: true,
  analytics: true,
}

// Session-scoped fallback for users whose browser blocks localStorage
// (Safari + "Block all cookies", sandboxed iframes). Without this, every
// banner dismissal is a no-op and the banner reappears on every render.
let inMemoryConsent: boolean | null = null
let inMemoryPreferences: CookiePreferences | null = null

function getCookieConsent(): boolean | null {
  const consent = safeGetItem(COOKIE_CONSENT_KEY)
  if (!consent) return inMemoryConsent
  try {
    return JSON.parse(consent)
  } catch {
    return inMemoryConsent
  }
}

function setCookieConsent(consent: boolean): void {
  inMemoryConsent = consent
  safeSetItem(COOKIE_CONSENT_KEY, JSON.stringify(consent))
}

function getCookiePreferences(): CookiePreferences {
  const prefs = safeGetItem(COOKIE_PREFERENCES_KEY)
  if (!prefs) return inMemoryPreferences ?? defaultPreferences
  try {
    return { ...defaultPreferences, ...JSON.parse(prefs) }
  } catch {
    return inMemoryPreferences ?? defaultPreferences
  }
}

function setCookiePreferences(prefs: CookiePreferences): void {
  inMemoryPreferences = prefs
  safeSetItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs))
}

export function useCookiePreferences(): CookiePreferences {
  const [prefs] = useState<CookiePreferences>(() => {
    // Lazy initialization to avoid hydration mismatch
    if (typeof window === 'undefined') return defaultPreferences
    return getCookiePreferences()
  })

  return prefs
}

export function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const t = useTranslations('landing')
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    // Lazy initialization to load existing preferences
    if (typeof window === 'undefined') return defaultPreferences
    return getCookiePreferences()
  })

  // Restore PostHog capture for returning users who previously accepted analytics,
  // and opt in native mobile users (cookie banner is suppressed in native WebViews).
  useEffect(() => {
    if (isNativePlatform()) {
      posthog.opt_in_capturing()
      return
    }
    const hasConsent = getCookieConsent()
    const prefs = getCookiePreferences()
    if (hasConsent && prefs.analytics) {
      posthog.opt_in_capturing()
    }
  }, [])

  useEffect(() => {
    // Skip cookie consent entirely on native apps (not legally required
    // inside a first-party WebView and wastes screen space).
    // isNativePlatform() uses multiple detection strategies including
    // navigator.standalone for iOS WKWebView and user-agent for Android WebView.
    if (isNativePlatform()) return

    // In Capacitor remote mode, the JS bridge may not be injected yet when
    // React mounts. Wait briefly, reset the cached detection, and re-check
    // before deciding to show the banner.
    const bridgeCheckTimer = setTimeout(() => {
      resetPlatformCache()
      if (isNativePlatform()) return

      // Check if user has already consented
      const hasConsent = getCookieConsent()
      if (hasConsent === null) {
        // Show banner after a short delay for better UX
        setIsVisible(true)
      }
    }, 500)

    return () => clearTimeout(bridgeCheckTimer)
  }, [])

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
    }
    setCookieConsent(true)
    setCookiePreferences(allAccepted)
    setPreferences(allAccepted)
    posthog.opt_in_capturing()
    setIsVisible(false)
  }

  const handleRejectNonEssential = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
    }
    setCookieConsent(true)
    setCookiePreferences(essentialOnly)
    setPreferences(essentialOnly)
    posthog.opt_out_capturing()
    setIsVisible(false)
  }

  const handleSavePreferences = () => {
    setCookieConsent(true)
    setCookiePreferences(preferences)
    if (preferences.analytics) {
      posthog.opt_in_capturing()
    } else {
      posthog.opt_out_capturing()
    }
    setIsVisible(false)
  }

  const handleClose = () => {
    // Dismiss the banner without opting out — user didn't make an explicit
    // choice, so we respect the current capture state (opted-in by default).
    setCookieConsent(true)
    setCookiePreferences(preferences)
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
      <Card className="max-w-2xl mx-auto shadow-lg border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">{t('cookies.title')}</CardTitle>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <CardDescription>{t('cookies.description')}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Expandable Details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? t('cookies.hideDetails') : t('cookies.showDetails')}
          </button>

          {showDetails && (
            <div className="space-y-4 pt-2 border-t">
              {/* Essential Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    <Label className="font-medium">{t('cookies.essential')}</Label>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      {t('cookies.alwaysOn')}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.essentialDescription')}
                  </p>
                </div>
                <Switch checked disabled className="opacity-50" />
              </div>

              {/* Functional Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="functional" className="font-medium">
                    {t('cookies.functional')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.functionalDescription')}
                  </p>
                </div>
                <Switch
                  id="functional"
                  checked={preferences.functional}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, functional: checked }))
                  }
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <Label htmlFor="analytics" className="font-medium">
                    {t('cookies.analytics')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('cookies.analyticsDescription')}
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, analytics: checked }))
                  }
                />
              </div>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex flex-col sm:flex-row gap-2 pt-2">
          {showDetails ? (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="w-full sm:w-auto"
              >
                {t('cookies.essentialOnly')}
              </Button>
              <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
                {t('cookies.savePreferences')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleRejectNonEssential}
                className="w-full sm:w-auto"
              >
                {t('cookies.rejectNonEssential')}
              </Button>
              <Button onClick={handleAcceptAll} className="w-full sm:w-auto">
                {t('cookies.acceptAll')}
              </Button>
            </>
          )}
          <Link
            href="/policies/privacy"
            target="_blank"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors text-center sm:ml-auto"
          >
            {t('cookies.privacyPolicy')}
          </Link>
        </CardFooter>
      </Card>
    </div>
  )
}

/**
 * Re-show the cookie consent banner (for use in settings)
 */
export function resetCookieConsent() {
  // Clear both layers so the banner reappears even on blocked-storage clients
  // where in-memory is the source of truth. The reload below makes this
  // belt-and-braces today, but keeps the reset honest if a future caller
  // wants to re-show the banner inline without a reload.
  inMemoryConsent = null
  inMemoryPreferences = null
  safeRemoveItem(COOKIE_CONSENT_KEY)
  window.location.reload()
}
